import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatDate, formatErrorDetail } from "@/lib/api";
import { Empty, PageHeader, Surface } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, ArrowLeft, FileText, Upload, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
    "employment_contract",
    "valid_id",
    "tin",
    "sss",
    "philhealth",
    "pagibig",
    "bank_account",
    "resume",
    "certificate",
    "medical",
    "clearance",
    "other",
];

const SELECT_CLS = "w-full h-10 px-3 rounded-md border border-border bg-background text-sm";
const PAYMENT_METHODS = [["", "Bank transfer (default)"], ["bank_transfer", "Bank transfer"], ["cash", "Cash"], ["cheque", "Cheque"], ["e_wallet", "E-wallet"]];
const FREQUENCIES = [["", "Every run of the company"], ["weekly", "Weekly"], ["semi_monthly", "Semi-monthly"], ["monthly", "Monthly"]];
const SALARY_TYPES = [["monthly", "Monthly"], ["daily", "Daily"], ["hourly", "Hourly"]];
const ALLOWANCE_CATEGORIES = [["transportation", "Transportation"], ["meal", "Meal"], ["communication", "Communication"], ["clothing", "Clothing"], ["rice", "Rice"], ["education", "Education"], ["other", "Other"]];
const ALLOWANCE_MODES = [["fixed", "Fixed amount a month"], ["percentage", "Percentage of basic"], ["attendance_based", "Per day attended"], ["performance_based", "Performance based"]];
const HAZARD_MODES = [["", "No hazard pay"], ["fixed", "Fixed amount a month"], ["percentage", "Percentage of basic"], ["per_day", "Per day worked"]];
const money = (v) => Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const describeChange = (c) => {
    const line = (a) => {
        const how = a.mode === "percentage" ? `${a.percent}% of basic`
            : a.mode === "attendance_based" ? `${money(a.amount)} a day attended`
            : a.mode === "performance_based" ? `${money(a.amount)} at ${a.performance_pct}%`
            : money(a.amount);
        return `${a.name}${a.category ? ` (${a.category})` : ""} ${how}`;
    };
    const hz = (h) => (!h || !h.mode ? "none" : h.mode === "percentage" ? `${h.percent}% of basic` : h.mode === "per_day" ? `${money(h.amount)} a day worked` : `${money(h.amount)} a month`);
    const f = (v) => (c.field === "allowances"
        ? ((v || []).map(line).join(", ") || "none")
        : c.field === "hazard_pay" ? hz(v)
        : (typeof v === "number" ? money(v) : (v ?? "none")));
    return `${c.field.replaceAll("_", " ")}: ${f(c.old)} to ${f(c.new)}`;
};

const nice = (s) => String(s || "—").replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function EmployeeDetail() {
    const { id } = useParams();
    const [emp, setEmp] = useState(null);
    const [saving, setSaving] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [docType, setDocType] = useState("other");
    const [docExpiry, setDocExpiry] = useState("");
    const [learning, setLearning] = useState(null);
    const [salaryReason, setSalaryReason] = useState("");
    const [salaryHistory, setSalaryHistory] = useState(null);
    useEffect(() => {
        if (id) api.get(`/learning/employees/${id}/record`).then((r) => setLearning(r.data)).catch(() => {});
    }, [id]);

    const load = async () => {
        const { data } = await api.get(`/hris/employees/${id}`);
        setEmp(data);
    };

    const loadDocuments = async () => {
        try {
            const { data } = await api.get(`/hris/employees/${id}/documents`);
            setDocuments(data.items || []);
        } catch {
            setDocuments([]);
        }
    };

    const loadSalaryHistory = async () => {
        try {
            const { data } = await api.get(`/hris/employees/${id}/salary-history`);
            setSalaryHistory(data.items || []);
        } catch {
            setSalaryHistory(null); // roles that may not see pay history get no history panel
        }
    };

    useEffect(() => {
        load();
        loadDocuments();
        loadSalaryHistory();
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/hris/employees/${id}`, { ...emp, salary_change_reason: salaryReason });
            toast.success("Employee updated");
            setSalaryReason("");
            load();
            loadSalaryHistory();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const uploadDocument = async (file) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        form.append("doc_type", docType);
        if (docExpiry) form.append("expiry_date", docExpiry);

        try {
            await api.post(`/hris/employees/${id}/documents`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Document uploaded and extracted");
            setDocExpiry("");
            loadDocuments();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const acknowledgeDocument = async (documentId) => {
        try {
            await api.post(`/hris/employees/documents/${documentId}/acknowledge`, {});
            toast.success("Document acknowledged");
            loadDocuments();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const verifyDocument = async (documentId) => {
        try {
            await api.post(`/hris/employees/documents/${documentId}/verify`, {
                notes: "Verified from employee document vault.",
            });
            toast.success("Document verified");
            loadDocuments();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    const rejectDocument = async (documentId) => {
        const reason = window.prompt("Reason for rejection?");
        if (!reason) return;

        try {
            await api.post(`/hris/employees/documents/${documentId}/reject`, { reason });
            toast.success("Document rejected");
            loadDocuments();
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail));
        }
    };

    if (!emp) return <div className="text-sm text-muted-foreground">Loading…</div>;

    const setField = (path, val) => {
        setEmp((cur) => {
            const next = { ...cur };
            const parts = path.split(".");
            let ref = next;
            for (let i = 0; i < parts.length - 1; i++) {
                ref[parts[i]] = { ...(ref[parts[i]] || {}) };
                ref = ref[parts[i]];
            }
            ref[parts[parts.length - 1]] = val;
            return next;
        });
    };

    return (
        <div>
            <Link to="/hris/employees" className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-2" data-testid="back-to-employees">
                <ArrowLeft className="size-3" /> Back to employees
            </Link>

            <PageHeader
                title={`${emp.last_name}, ${emp.first_name}${emp.middle_name ? ` ${emp.middle_name}` : ""}`}
                subtitle={`${emp.employee_no} · ${emp.position || "—"}`}
                actions={
                    <div className="flex items-center gap-2">
                        <StatusBadge value={emp.employment_status} />
                        <StatusBadge value={emp.employment_type} />
                        <button onClick={save} disabled={saving} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="emp-save-btn">
                            <Save className="size-4" /> {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                }
            />

            <Tabs defaultValue="personal">
                <TabsList>
                    <TabsTrigger value="personal">Personal</TabsTrigger>
                    <TabsTrigger value="employment">Employment</TabsTrigger>
                    <TabsTrigger value="salary">Salary & Allowances</TabsTrigger>
                    <TabsTrigger value="gov">Government IDs</TabsTrigger>
                    <TabsTrigger value="bank">Bank</TabsTrigger>
                    <TabsTrigger value="dependents">Dependents</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                    <TabsTrigger value="learning" data-testid="tab-learning">Learning{learning ? ` (${learning.completed}/${learning.assigned})` : ""}</TabsTrigger>
                </TabsList>

                <TabsContent value="learning">
                    <Surface className="p-4">
                        {learning && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                                {[["Assigned", learning.assigned], ["Completed", learning.completed],
                                  ["Completion", learning.completion_rate != null ? `${learning.completion_rate}%` : "—"],
                                  ["Avg quiz score", learning.avg_quiz_score ?? "—"],
                                  ["Certificates", learning.certificates]].map(([l, v]) => (
                                    <div key={l} className="border border-border rounded-md p-2 text-center">
                                        <div className="text-[10px] uppercase text-muted-foreground">{l}</div>
                                        <div className="font-display text-lg font-bold">{v}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <table className="w-full dense-table">
                            <thead><tr><th>Course</th><th>Category</th><th>Status</th><th className="num">Quiz Score</th><th>Certificate</th><th>Completed</th></tr></thead>
                            <tbody>
                                {(learning?.items || []).map((l, i) => (
                                    <tr key={i} data-testid={`emp-learning-${i}`}>
                                        <td className="font-medium">{l.course}</td>
                                        <td className="text-xs capitalize">{l.category || "—"}</td>
                                        <td><StatusBadge value={l.status} /></td>
                                        <td className="num font-mono">{l.quiz_score ?? "—"}{l.quiz_passed === false && <span className="text-destructive text-[10px]"> failed</span>}</td>
                                        <td className="font-mono text-xs">{l.certificate_no || "—"}</td>
                                        <td className="text-xs">{l.completed_at ? formatDate(l.completed_at) : "—"}</td>
                                    </tr>
                                ))}
                                {learning && !learning.items.length && <tr><td colSpan={6}><Empty title="No trainings assigned yet" /></td></tr>}
                            </tbody>
                        </table>
                    </Surface>
                </TabsContent>

                <TabsContent value="personal">
                    <Surface className="p-5 mt-2 grid grid-cols-2 lg:grid-cols-3 gap-3">
                        <div><Label>First Name</Label><Input value={emp.first_name || ""} onChange={(e) => setField("first_name", e.target.value)} /></div>
                        <div><Label>Middle Name</Label><Input value={emp.middle_name || ""} onChange={(e) => setField("middle_name", e.target.value)} /></div>
                        <div><Label>Last Name</Label><Input value={emp.last_name || ""} onChange={(e) => setField("last_name", e.target.value)} /></div>
                        <div><Label>Email</Label><Input value={emp.email || ""} onChange={(e) => setField("email", e.target.value)} /></div>
                        <div><Label>Phone</Label><Input value={emp.phone || ""} onChange={(e) => setField("phone", e.target.value)} /></div>
                        <div><Label>Birth Date</Label><Input type="date" value={emp.birth_date || ""} onChange={(e) => setField("birth_date", e.target.value)} /></div>
                        <div><Label>Gender</Label><Input value={emp.gender || ""} onChange={(e) => setField("gender", e.target.value)} /></div>
                        <div><Label>Civil Status</Label><Input value={emp.civil_status || ""} onChange={(e) => setField("civil_status", e.target.value)} /></div>
                        <div><Label>Nationality</Label><Input value={emp.nationality || ""} onChange={(e) => setField("nationality", e.target.value)} data-testid="emp-nationality" /></div>
                        <div className="col-span-2"><Label>Address</Label><Input value={emp.address || ""} onChange={(e) => setField("address", e.target.value)} /></div>
                        <div className="col-span-2 lg:col-span-3 text-xs font-semibold uppercase text-muted-foreground mt-2">Emergency contact</div>
                        <div><Label>Name</Label><Input value={emp.emergency_contact?.name || ""} onChange={(e) => setField("emergency_contact.name", e.target.value)} data-testid="emp-ec-name" /></div>
                        <div><Label>Relationship</Label><Input value={emp.emergency_contact?.relationship || ""} onChange={(e) => setField("emergency_contact.relationship", e.target.value)} data-testid="emp-ec-relationship" /></div>
                        <div><Label>Phone</Label><Input value={emp.emergency_contact?.phone || ""} onChange={(e) => setField("emergency_contact.phone", e.target.value)} data-testid="emp-ec-phone" /></div>
                    </Surface>
                </TabsContent>

                <TabsContent value="employment">
                    <Surface className="p-5 mt-2 grid grid-cols-2 lg:grid-cols-3 gap-3">
                        <div><Label>Employee No.</Label><Input value={emp.employee_no || ""} disabled /></div>
                        <div><Label>Biometric ID</Label><Input value={emp.biometric_id || ""} placeholder="Terminal enrolment no." onChange={(e) => setField("biometric_id", e.target.value)} data-testid="emp-biometric-id" /></div>
                        <div><Label>Position</Label><Input value={emp.position || ""} onChange={(e) => setField("position", e.target.value)} /></div>
                        <div><Label>Employment Type</Label><Input value={emp.employment_type || ""} onChange={(e) => setField("employment_type", e.target.value)} /></div>
                        <div><Label>Employment Status</Label><Input value={emp.employment_status || ""} onChange={(e) => setField("employment_status", e.target.value)} /></div>
                        <div><Label>Hire Date</Label><Input type="date" value={emp.hire_date || ""} onChange={(e) => setField("hire_date", e.target.value)} /></div>
                        <div><Label>Regularization Date</Label><Input type="date" value={emp.regularization_date || ""} onChange={(e) => setField("regularization_date", e.target.value)} /></div>
                        <div><Label>Work Location</Label><Input value={emp.work_location || ""} placeholder="Site or office where the person works" onChange={(e) => setField("work_location", e.target.value)} data-testid="emp-work-location" /></div>
                        <div>
                            <Label>Payroll Frequency</Label>
                            <select className={SELECT_CLS} value={emp.payroll_frequency || ""} onChange={(e) => setField("payroll_frequency", e.target.value || null)} data-testid="emp-payroll-frequency">
                                {FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <div className="text-[10px] text-muted-foreground mt-0.5">A weekly employee is paid only on weekly runs.</div>
                        </div>
                    </Surface>
                </TabsContent>

                <TabsContent value="salary">
                    <Surface className="p-5 mt-2 grid grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                            <Label>Salary Type</Label>
                            <select className={SELECT_CLS} value={emp.salary?.salary_type || "monthly"} onChange={(e) => setField("salary.salary_type", e.target.value)} data-testid="emp-salary-type">
                                {SALARY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Daily or hourly: type that rate and the other two are worked out when you save.</div>
                        </div>
                        <div>
                            <Label>Pay basis</Label>
                            <select className={SELECT_CLS} value={emp.pay_basis || ""} onChange={(e) => setField("pay_basis", e.target.value || null)} data-testid="emp-pay-basis">
                                <option value="">Follow the payroll run</option>
                                <option value="monthly">Monthly (absences deducted)</option>
                                <option value="daily">Daily (paid for days worked)</option>
                            </select>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Daily: daily rate x days worked on any run, late minutes deducted. Monthly: half the month less absences. Blank keeps the old behaviour.</div>
                        </div>
                        <div><Label>Basic Monthly</Label><Input type="number" value={emp.salary?.basic_monthly || emp.base_salary || 0} onChange={(e) => setField("salary.basic_monthly", Number(e.target.value))} disabled={["daily", "hourly"].includes(emp.salary?.salary_type)} data-testid="emp-basic-monthly" /></div>
                        <div><Label>Daily Rate</Label><Input type="number" value={emp.salary?.daily_rate || 0} onChange={(e) => setField("salary.daily_rate", Number(e.target.value))} disabled={emp.salary?.salary_type === "hourly"} data-testid="emp-daily-rate" /></div>
                        <div><Label>Hourly Rate</Label><Input type="number" value={emp.salary?.hourly_rate || 0} onChange={(e) => setField("salary.hourly_rate", Number(e.target.value))} disabled={emp.salary?.salary_type === "daily"} data-testid="emp-hourly-rate" /></div>
                        <div className="col-span-2">
                            <Label>Reason for a salary change</Label>
                            <Input value={salaryReason} placeholder="Required when a rate, allowance, hazard pay or the pay basis changes, for example annual increase" onChange={(e) => setSalaryReason(e.target.value)} data-testid="emp-salary-reason" />
                        </div>
                    </Surface>
                    <Surface className="p-5 mt-3" data-testid="allowances-editor">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-sm">Allowances</h3>
                            <button type="button" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-muted" data-testid="allowance-add"
                                onClick={() => setField("salary.allowances", [...(emp.salary?.allowances || []), { name: "", category: "other", mode: "fixed", amount: 0, taxable: false }])}>
                                <Plus size={12} /> Add allowance
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">Paid on the Allowance run. A taxable allowance also raises the base that SSS, PhilHealth and Pag-IBIG are worked out on. Per day attended pays only the days the employee was present; performance based pays the monthly target at the rating you enter, so update the rating each period.</p>
                        {(emp.salary?.allowances || []).map((a, i) => {
                            const mode = a.mode || "fixed";
                            const upd = (patch) => setField("salary.allowances", (emp.salary?.allowances || []).map((x, j) => (j === i ? { ...x, category: x.category || "other", mode: x.mode || "fixed", ...patch } : x)));
                            const basic = Number(emp.salary?.basic_monthly || 0);
                            const monthly = mode === "percentage" ? basic * Number(a.percent || 0) / 100 : mode === "performance_based" ? Number(a.amount || 0) * Number(a.performance_pct || 0) / 100 : mode === "fixed" ? Number(a.amount || 0) : null;
                            return (
                                <div key={i} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-end mb-2" data-testid={`allowance-row-${i}`}>
                                    <div className="lg:col-span-2"><Label className="text-[10px]">Name</Label><Input value={a.name || ""} onChange={(e) => upd({ name: e.target.value })} data-testid={`allowance-name-${i}`} /></div>
                                    <div className="lg:col-span-2"><Label className="text-[10px]">Type</Label>
                                        <select className={SELECT_CLS} value={a.category || "other"} onChange={(e) => upd({ category: e.target.value })} data-testid={`allowance-category-${i}`}>
                                            {ALLOWANCE_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select></div>
                                    <div className="lg:col-span-3"><Label className="text-[10px]">How it is worked out</Label>
                                        <select className={SELECT_CLS} value={mode} onChange={(e) => upd({ mode: e.target.value })} data-testid={`allowance-mode-${i}`}>
                                            {ALLOWANCE_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select></div>
                                    {mode === "percentage" ? (
                                        <div className="lg:col-span-2"><Label className="text-[10px]">Percent of basic</Label><Input type="number" step="0.01" value={a.percent ?? ""} onChange={(e) => upd({ percent: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`allowance-percent-${i}`} /></div>
                                    ) : (
                                        <div className="lg:col-span-2"><Label className="text-[10px]">{mode === "attendance_based" ? "Amount a day attended" : mode === "performance_based" ? "Monthly target" : "Amount a month"}</Label><Input type="number" step="0.01" value={a.amount ?? ""} onChange={(e) => upd({ amount: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`allowance-amount-${i}`} /></div>
                                    )}
                                    {mode === "performance_based" && (
                                        <div className="lg:col-span-1"><Label className="text-[10px]">Rating %</Label><Input type="number" min="0" max="100" value={a.performance_pct ?? ""} onChange={(e) => upd({ performance_pct: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`allowance-rating-${i}`} /></div>
                                    )}
                                    <label className="lg:col-span-1 flex items-center gap-1 text-xs pb-2"><input type="checkbox" checked={!!a.taxable} onChange={(e) => upd({ taxable: e.target.checked })} data-testid={`allowance-taxable-${i}`} /> Taxable</label>
                                    <button type="button" className="lg:col-span-1 pb-2 text-muted-foreground hover:text-destructive" aria-label="Remove allowance" data-testid={`allowance-remove-${i}`}
                                        onClick={() => setField("salary.allowances", (emp.salary?.allowances || []).filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                                    {monthly !== null && monthly > 0 && <div className="col-span-2 lg:col-span-12 text-[10px] text-muted-foreground -mt-1">About {money(monthly)} a month</div>}
                                </div>
                            );
                        })}
                        {!(emp.salary?.allowances || []).length && <div className="text-xs text-muted-foreground mb-2">No allowances.</div>}
                        <div className="border-t border-border mt-3 pt-3 grid grid-cols-2 lg:grid-cols-6 gap-2 items-end" data-testid="hazard-editor">
                            <div className="lg:col-span-2"><Label>Hazard pay</Label>
                                <select className={SELECT_CLS} value={emp.salary?.hazard_pay?.mode || ""} data-testid="hazard-mode"
                                    onChange={(e) => setField("salary.hazard_pay", e.target.value ? { mode: e.target.value, amount: emp.salary?.hazard_pay?.amount ?? 0, percent: emp.salary?.hazard_pay?.percent ?? 0 } : null)}>
                                    {HAZARD_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select></div>
                            {emp.salary?.hazard_pay?.mode === "percentage" && <div><Label className="text-[10px]">Percent of basic</Label><Input type="number" step="0.01" value={emp.salary.hazard_pay.percent ?? ""} onChange={(e) => setField("salary.hazard_pay", { ...emp.salary.hazard_pay, percent: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="hazard-percent" /></div>}
                            {(emp.salary?.hazard_pay?.mode === "fixed" || emp.salary?.hazard_pay?.mode === "per_day") && <div><Label className="text-[10px]">{emp.salary.hazard_pay.mode === "per_day" ? "Amount a day worked" : "Amount a month"}</Label><Input type="number" step="0.01" value={emp.salary.hazard_pay.amount ?? ""} onChange={(e) => setField("salary.hazard_pay", { ...emp.salary.hazard_pay, amount: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="hazard-amount" /></div>}
                            <div className="col-span-2 lg:col-span-3 text-[10px] text-muted-foreground pb-2">Taxable, paid on the basic run, reduced for absence. Counted in the SSS, PhilHealth and Pag-IBIG base. A minimum wage earner is exempt from withholding tax on it.</div>
                        </div>
                    </Surface>
                    {salaryHistory && (
                        <Surface className="p-5 mt-3" data-testid="salary-history">
                            <h3 className="font-semibold text-sm mb-1">Salary history</h3>
                            <p className="text-xs text-muted-foreground mb-3">Every change to the rates or allowances, newest first. Entries are never edited or removed.</p>
                            <table className="w-full dense-table">
                                <thead><tr><th>When</th><th>Changed by</th><th>What changed</th><th>Reason</th></tr></thead>
                                <tbody>
                                    {salaryHistory.map((h) => (
                                        <tr key={h.id} data-testid={`salary-history-${h.id}`}>
                                            <td className="text-xs whitespace-nowrap">{formatDate(h.changed_at)}</td>
                                            <td className="text-xs">{h.changed_by_name || "—"}</td>
                                            <td className="text-xs">{(h.changes || []).map((c) => <div key={c.field}>{describeChange(c)}</div>)}</td>
                                            <td className="text-xs">{h.reason || "—"}</td>
                                        </tr>
                                    ))}
                                    {!salaryHistory.length && <tr><td colSpan={4}><Empty title="No salary changes recorded yet" subtitle="The first change to a rate or allowance starts the history." /></td></tr>}
                                </tbody>
                            </table>
                        </Surface>
                    )}
                </TabsContent>

                <TabsContent value="gov">
                    <Surface className="p-5 mt-2 grid grid-cols-2 lg:grid-cols-3 gap-3">
                        <div><Label>SSS</Label><Input value={emp.government_ids?.sss || ""} onChange={(e) => setField("government_ids.sss", e.target.value)} /></div>
                        <div><Label>PhilHealth</Label><Input value={emp.government_ids?.philhealth || ""} onChange={(e) => setField("government_ids.philhealth", e.target.value)} /></div>
                        <div><Label>Pag-IBIG</Label><Input value={emp.government_ids?.pagibig || ""} onChange={(e) => setField("government_ids.pagibig", e.target.value)} /></div>
                        <div><Label>TIN</Label><Input value={emp.government_ids?.tin || ""} onChange={(e) => setField("government_ids.tin", e.target.value)} /></div>
                        <div><Label>RDO Code</Label><Input value={emp.government_ids?.rdo_code || ""} onChange={(e) => setField("government_ids.rdo_code", e.target.value)} /></div>
                    </Surface>
                </TabsContent>

                <TabsContent value="bank">
                    <Surface className="p-5 mt-2 grid grid-cols-2 lg:grid-cols-3 gap-3">
                        <div><Label>Bank Name</Label><Input value={emp.bank?.bank_name || ""} onChange={(e) => setField("bank.bank_name", e.target.value)} /></div>
                        <div><Label>Account Number</Label><Input value={emp.bank?.account_number || ""} onChange={(e) => setField("bank.account_number", e.target.value)} /></div>
                        <div><Label>Account Name</Label><Input value={emp.bank?.account_name || ""} onChange={(e) => setField("bank.account_name", e.target.value)} /></div>
                        <div>
                            <Label>Preferred Payment Method</Label>
                            <select className={SELECT_CLS} value={emp.payment_method || ""} onChange={(e) => setField("payment_method", e.target.value || null)} data-testid="emp-payment-method">
                                {PAYMENT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Cash, cheque and e-wallet employees are left out of the bank file.</div>
                        </div>
                    </Surface>
                </TabsContent>

                <TabsContent value="dependents">
                    <Surface className="p-5 mt-2">
                        <button className="text-xs text-primary" onClick={() => setField("dependents", [...(emp.dependents || []), { name: "", relationship: "" }])}>+ Add dependent</button>
                    </Surface>
                </TabsContent>

                <TabsContent value="documents">
                    <Surface className="p-5 mt-2">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
                            <div>
                                <h3 className="font-semibold flex items-center gap-2">
                                    <FileText className="size-4" /> Employee Document Vault
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    OCR/Textract-backed employee file with SHA256 verification and detected government IDs.
                                </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <select className="h-9 px-3 rounded-md border border-border text-sm" value={docType} onChange={(e) => setDocType(e.target.value)}>
                                    {DOC_TYPES.map((x) => <option key={x} value={x}>{nice(x)}</option>)}
                                </select>
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground">Expires</span>
                                    <input type="date" className="h-9 px-2 rounded-md border border-border text-sm" value={docExpiry} onChange={(e) => setDocExpiry(e.target.value)} title="Optional expiry date for renewal tracking" data-testid="doc-expiry" />
                                </div>
                                <label className="h-9 px-3 rounded-md bg-primary text-white text-sm cursor-pointer inline-flex items-center gap-2">
                                    <Upload className="size-4" />
                                    Upload
                                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" onChange={(e) => uploadDocument(e.target.files?.[0])} />
                                </label>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {documents.map((d) => (
                                <div key={d.id} className="rounded-xl border bg-white p-4">
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                        <div>
                                            <div className="font-medium flex items-center gap-2">
                                                {d.filename}
                                                {d.version && <span className="badge badge-info text-[10px]">v{d.version}</span>}
                                                {d.is_current === false && <span className="badge badge-draft text-[10px]">superseded</span>}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {nice(d.doc_type)} · {d.extraction_method} · {formatDate(d.created_at)}
                                            </div>
                                            {d.expiry_date && (
                                                <div className={`text-xs mt-1 ${d.expiry_date < new Date().toISOString().slice(0, 10) ? "text-red-700 font-medium" : "text-muted-foreground"}`}>
                                                    {d.expiry_date < new Date().toISOString().slice(0, 10) ? "⚠ Expired" : "Expires"}: {d.expiry_date}
                                                </div>
                                            )}
                                            <div className="text-xs text-muted-foreground mt-1 font-mono break-all">
                                                SHA256: {d.sha256}
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <StatusBadge value={d.status || "uploaded"} />
                                            {d.acknowledged
                                                ? <span className="badge badge-info text-[10px]" title={d.acknowledged_at || ""}>acknowledged</span>
                                                : (
                                                    <button
                                                        className="h-8 px-3 rounded-md border border-border bg-background text-xs"
                                                        onClick={() => acknowledgeDocument(d.id)}
                                                        data-testid={`ack-doc-${d.id}`}
                                                    >
                                                        Acknowledge
                                                    </button>
                                                )}
                                            {d.status !== "verified" && (
                                                <button
                                                    className="h-8 px-3 rounded-md bg-emerald-600 text-white text-xs"
                                                    onClick={() => verifyDocument(d.id)}
                                                >
                                                    Verify
                                                </button>
                                            )}
                                            {d.status !== "rejected" && (
                                                <button
                                                    className="h-8 px-3 rounded-md bg-secondary text-xs"
                                                    onClick={() => rejectDocument(d.id)}
                                                >
                                                    Reject
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {!!Object.keys(d.analysis?.detected_ids || {}).length && (
                                        <div className="mt-3">
                                            <div className="text-xs uppercase font-semibold text-muted-foreground">Detected IDs</div>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {Object.entries(d.analysis.detected_ids).map(([k, v]) => (
                                                    <span key={k} className="text-xs px-2 py-1 rounded bg-secondary">
                                                        {nice(k)}: <span className="font-mono">{v}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!!d.analysis?.warnings?.length && (
                                        <div className="mt-3 text-xs text-amber-700">
                                            {d.analysis.warnings.map((w) => <div key={w}>⚠ {w}</div>)}
                                        </div>
                                    )}

                                                                        {d.verified_at && (
                                        <div className="mt-3 text-xs text-emerald-700">
                                            Verified at {d.verified_at}
                                            {d.verification_notes ? ` · ${d.verification_notes}` : ""}
                                        </div>
                                    )}

                                    {d.rejection_reason && (
                                        <div className="mt-3 text-xs text-red-700">
                                            Rejected: {d.rejection_reason}
                                        </div>
                                    )}

                                    {d.extracted_text && (
                                        <details className="mt-3">
                                            <summary className="text-xs text-primary cursor-pointer">View extracted text</summary>
                                            <div className="mt-2 max-h-48 overflow-auto rounded-md bg-secondary p-3 text-xs whitespace-pre-wrap">
                                                {d.extracted_text}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            ))}

                            {!documents.length && (
                                <Empty title="No documents yet" subtitle="Upload employee documents or attach proof from onboarding." />
                            )}
                        </div>
                    </Surface>
                </TabsContent>
            </Tabs>
        </div>
    );
}
